// SP-2 WS-7 — the per-loan Monthly Cycle Cockpit. One screen that walks a
// B3 through the month: the six-step stepper (server-derived states), ONE
// current-step card that says where things stand in plain language and
// offers exactly ONE permission-gated next action, and a plain-sentence
// narration of where a recorded payment's money went. Steps whose baton is
// external (servicer / borrower) get a sentence, never a button.

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";
import { Button, Card, EmptyState, Skeleton } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { useMonthlyCycle } from "@/hooks/portfolioAdmin/useMonthlyCycle";
import { monthlyCycleKeys } from "@/hooks/portfolioAdmin/loanStatementsKeys";
import {
  servicingKeys,
  useServicingLedger,
} from "@/hooks/portfolioAdmin/useServicing";
import { invalidateLoanHealth } from "@/hooks/portfolioAdmin/invalidation";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import { CycleStepper } from "../monthlyCycle/CycleStepper";
import {
  CYCLE_STEP_LABEL,
  currentStep,
  monthLabel,
  nextAction,
  statusSentence,
  type CycleActionKind,
} from "../monthlyCycle/cycleCopy";
import { GlossaryText } from "../monthlyCycle/GlossaryHint";
import { FirstRunTour } from "@/components/ux";
import { GenerateStatementModal } from "../monthlyCycle/GenerateStatementModal";
import { RegenerateStatementModal } from "../monthlyCycle/RegenerateStatementModal";
import { StatementReviewModal } from "../monthlyCycle/StatementReviewModal";
import { WaterfallNarrationCard } from "../monthlyCycle/WaterfallNarrationCard";
import { RecordPaymentWizard } from "../paymentTracking/RecordPaymentWizard";
import { ReversalConfirmationModal } from "@/components/payments/ReversalConfirmationModal";

interface MonthlyCycleTabProps {
  loan: LoanSummaryDto;
}

// First-run tour copy (SP-2b). The holder (Statements.Generate) version
// narrates the month's baton-pass; the read-only version orients everyone
// else. Jargon routes through GlossaryText.
const HOLDER_TOUR_STEPS = [
  {
    title: "A statement is generated",
    body: "Each month a statement is generated for this loan — that's the bill the borrower pays.",
  },
  {
    title: "You review and send it",
    body: "You review it and send it to the servicer.",
  },
  {
    title: "The servicer posts it",
    body: "The servicer approves and posts it — then the borrower pays.",
  },
  {
    title: "You record the payment",
    body: (
      <GlossaryText text="When the payment clears, you record it — the waterfall splits it across everyone owed a piece." />
    ),
  },
  {
    title: "The payout closes the cycle",
    body: "The payout goes back to the servicer to approve. That's one full cycle.",
  },
];

const READ_ONLY_TOUR_STEPS = [
  {
    title: "The stepper shows the month",
    body: "The stepper above tracks where this loan's billing month stands, from statement to payout.",
  },
  {
    title: "Who handles statements",
    body: "Statements come from the monthly worker and are reviewed by servicing ops (B3) before they go to the servicer.",
  },
  {
    title: "Payment and payout state",
    body: (
      <GlossaryText text="The current-step card and the cards below show payment and payout state, including how the waterfall split a recorded payment." />
    ),
  },
];

type OpenModal = CycleActionKind | null;

export function MonthlyCycleTab({ loan }: MonthlyCycleTabProps) {
  const auth = useAuth();
  const qc = useQueryClient();
  const { date } = useDateFormatter();
  const isAdmin = auth.hasRoleCode("ADMIN");
  const canRecord = auth.hasPermission("Payments.Create") || isAdmin;
  const canReverse = auth.hasPermission("Payments.Reverse") || isAdmin;

  const [period, setPeriod] = useState<string | undefined>(undefined);
  const cycleQuery = useMonthlyCycle(loan.id, period);
  const cycle = cycleQuery.data;
  // Period options for the prefilled record modal — shares the Payment
  // Tracking tab's cache key, so this is usually already warm.
  const ledgerQuery = useServicingLedger(loan.id);

  const [openModal, setOpenModal] = useState<OpenModal>(null);

  // ?action= deep links (record-cleared / reverse-payment), consumed once —
  // the PaymentTrackingTab ?action=record-payment precedent. The prefill is
  // async, so the request is held until the cycle payload lands.
  const [params, setParams] = useSearchParams();
  const [pendingAction, setPendingAction] = useState<string | null>(
    () => params.get("action"),
  );
  useEffect(() => {
    const action = params.get("action");
    if (action !== "record-cleared" && action !== "reverse-payment") return;
    const updated = new URLSearchParams(params);
    updated.delete("action");
    setParams(updated, { replace: true });
  }, [params, setParams]);
  useEffect(() => {
    if (!pendingAction || !cycle) return;
    if (pendingAction === "record-cleared" && cycle.recordPrefill && canRecord) {
      setOpenModal("record");
    }
    if (pendingAction === "reverse-payment" && cycle.reversalPrefill && canReverse) {
      setOpenModal("reverse");
    }
    setPendingAction(null);
  }, [pendingAction, cycle, canRecord, canReverse]);

  // Recording/reversing a payment moves the cycle's steps AND the servicing
  // ledger; loan health is the single severity source for the tab badges.
  const refreshMoneyScope = () => {
    void qc.invalidateQueries({ queryKey: monthlyCycleKeys.loanPrefix(loan.id) });
    void qc.invalidateQueries({ queryKey: servicingKeys.ledger(loan.id) });
    void invalidateLoanHealth(qc, loan.id);
  };

  if (cycleQuery.isLoading) {
    return (
      <div className="flex flex-col gap-space-4" data-testid="monthly-cycle-loading">
        <Card>
          <Skeleton className="h-8 w-1/2" />
        </Card>
        <Card>
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-2/3 mt-space-2" />
        </Card>
      </div>
    );
  }

  if (cycleQuery.isError || !cycle) {
    return (
      <Card data-testid="monthly-cycle-error">
        <p className="text-small text-status-error m-0 mb-space-2">
          Couldn&apos;t load this loan&apos;s cycle. Refresh to try again.
        </p>
        <Button variant="secondary" size="sm" onClick={() => void cycleQuery.refetch()}>
          Retry
        </Button>
      </Card>
    );
  }

  if (!cycle.tpsReady) {
    return (
      <div data-testid="monthly-cycle-not-ready">
        <EmptyState
          icon={CalendarClock}
          title="Not set up for monthly billing yet"
          description="This loan isn't set up for monthly billing yet — it needs a servicing platform (TPS) assigned. An administrator can assign one from the loan's Overview tab."
        />
      </div>
    );
  }

  // SP-3 — assigned to a real servicer that hasn't finished onboarding:
  // teach where the blocker lives instead of rendering the cockpit steps.
  if (cycle.tpsSetupIncomplete) {
    return (
      <div data-testid="monthly-cycle-setup-incomplete">
        <EmptyState
          icon={CalendarClock}
          title="Servicer setup incomplete"
          description={`This loan's servicing platform (${cycle.tpsName ?? "TPS"}) hasn't finished onboarding — statements can't go out until its setup checklist is complete.`}
        >
          {loan.tpsId && (
            <Link
              to={`/portfolio-admin/tps/${loan.tpsId}`}
              className="text-gold-shadow underline text-small"
              data-testid="monthly-cycle-tps-setup-link"
            >
              Open the TPS setup checklist
            </Link>
          )}
        </EmptyState>
      </div>
    );
  }

  const step = currentStep(cycle) ?? cycle.steps[cycle.steps.length - 1] ?? null;
  const action = step ? nextAction(cycle, step) : null;
  const actionPermitted =
    action != null &&
    (auth.hasPermission(action.permission) || isAdmin);

  const openFor = (kind: CycleActionKind) => setOpenModal(kind);

  return (
    <div className="flex flex-col gap-space-4" data-testid="monthly-cycle-tab">
      <FirstRunTour
        tourKey="tour-monthly-cycle"
        steps={
          auth.hasPermission("Statements.Generate")
            ? HOLDER_TOUR_STEPS
            : READ_ONLY_TOUR_STEPS
        }
      />
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-space-3 mb-space-4">
          <div className="flex items-center gap-space-3">
            <h3 className="font-display font-bold text-h3 m-0">Monthly cycle</h3>
            <select
              aria-label="Billing month"
              className="border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure"
              value={cycle.period}
              onChange={(e) => setPeriod(e.target.value)}
              data-testid="cycle-period-select"
            >
              {cycle.availablePeriods.map((p) => (
                <option key={p} value={p}>
                  {monthLabel(p)}
                </option>
              ))}
            </select>
          </div>
          <p className="text-small text-gray-mid m-0" data-testid="cycle-tps-name">
            Servicing platform: {cycle.tpsName ?? "—"}
          </p>
        </div>
        <CycleStepper steps={cycle.steps} />
      </Card>

      {step && (
        <Card data-testid="current-step-card">
          <p className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-2">
            {CYCLE_STEP_LABEL[step.key]}
          </p>
          <p className="text-body text-onyx m-0" data-testid="current-step-sentence">
            <GlossaryText text={statusSentence(cycle, step, date)} />
          </p>
          {cycle.recordedViaFirstPayment && (
            <p
              className="text-small text-gray-mid m-0 mt-space-2"
              data-testid="cycle-recorded-via-first-payment"
            >
              This period&apos;s payment was already recorded via first-payment
              recording (100% to Phoenix, at boarding), so there&apos;s nothing to
              record here — the cycle moves straight to the payout.
            </p>
          )}
          {action && actionPermitted && (
            <div className="mt-space-3">
              <Button
                onClick={() => openFor(action.kind)}
                data-testid="current-step-action"
              >
                {action.label}
              </Button>
            </div>
          )}
          {action && !actionPermitted && (
            <p
              className="text-small text-gray-mid m-0 mt-space-3"
              data-testid="current-step-permission-note"
            >
              Someone with the {action.permission} permission takes it from here.
            </p>
          )}
        </Card>
      )}

      {cycle.servicingPayment && (
        <WaterfallNarrationCard servicingPayment={cycle.servicingPayment} />
      )}

      {openModal === "generate" && (
        <GenerateStatementModal
          loanId={loan.id}
          period={cycle.period}
          periodStart={cycle.periodStart}
          periodEnd={cycle.periodEnd}
          open
          onClose={() => setOpenModal(null)}
        />
      )}

      {openModal === "regenerate" && cycle.statement && (
        <RegenerateStatementModal
          loanId={loan.id}
          statementId={cycle.statement.id}
          period={cycle.period}
          returnedReason={cycle.statement.returnedReason}
          open
          onClose={() => setOpenModal(null)}
        />
      )}

      {openModal === "send" && cycle.statement && (
        <StatementReviewModal
          loanId={loan.id}
          statementId={cycle.statement.id}
          period={cycle.period}
          tpsName={cycle.tpsName}
          open
          onClose={() => setOpenModal(null)}
        />
      )}

      {openModal === "record" && cycle.recordPrefill && (
        <RecordPaymentWizard
          loan={loan}
          periods={ledgerQuery.data?.periods ?? []}
          nextPayablePeriod={ledgerQuery.data?.nextPayablePeriod ?? null}
          boardingFirstPaymentOnFile={
            ledgerQuery.data?.boardingFirstPaymentOnFile ?? false
          }
          open
          prefill={{
            amount: cycle.recordPrefill.amount,
            periodStart: cycle.recordPrefill.periodStart,
            externalReference: cycle.recordPrefill.externalReference,
            expectedAmount: cycle.recordPrefill.billAmountDue,
            payerName: cycle.recordPrefill.payerName,
          }}
          onClose={() => {
            setOpenModal(null);
            // The modal closes on cancel too — the extra refetch is cheap
            // and guarantees the stepper reflects a just-recorded payment.
            refreshMoneyScope();
          }}
        />
      )}

      <ReversalConfirmationModal
        open={openModal === "reverse" && cycle.reversalPrefill != null}
        onClose={() => setOpenModal(null)}
        paymentId={cycle.reversalPrefill?.servicingPaymentId ?? ""}
        initialReason={cycle.reversalPrefill?.suggestedReason}
        onReversed={() => refreshMoneyScope()}
      />
    </div>
  );
}
